/**
 * Compliance verification queue (US-031.4).
 *
 * Athletes in the caller's org(s) who have an NCAA ID but haven't been
 * verified yet. One-click verify; reject opens an inline reason input
 * (server requires non-empty reason, surfaced to the athlete via a
 * Notification).
 *
 * The list is server-prefetched so the queue paints with content (or the
 * empty state) without a client roundtrip.
 */
import { Button, Card, CardContent, CardHeader, CardTitle, Input, toast } from '@app/ui';
import { formatDate } from '@app/validators';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { api } from '../../../lib/api';

export const Route = createFileRoute('/_authenticated/compliance/verifications')({
  loader: async ({ context }) => {
    const { getServerApi } = await import('../../../lib/api-server');
    const serverApi = await getServerApi();
    await context.queryClient.ensureQueryData(serverApi.eligibility.listPending.queryOptions());
  },
  component: VerificationsPage,
});

function VerificationsPage() {
  const queryClient = useQueryClient();
  const { data } = useSuspenseQuery(api.eligibility.listPending.queryOptions());
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: api.eligibility.listPending.queryKey() });

  const verifyMutation = useMutation({
    ...api.eligibility.verify.mutationOptions(),
    onSuccess: (_res, vars) => {
      toast.success('Athlete verified');
      // Optimistic-ish: drop the verified row from the queue immediately
      // by invalidating; the next refetch is the source of truth.
      void vars;
      invalidate();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Verify failed'),
  });

  const rejectMutation = useMutation({
    ...api.eligibility.reject.mutationOptions(),
    onSuccess: () => {
      toast.success('Rejection sent — athlete has been notified.');
      setRejectingId(null);
      setRejectReason('');
      invalidate();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Reject failed'),
  });

  const handleVerify = (userId: string) => verifyMutation.mutate({ userId });
  const handleRejectSubmit = (userId: string) => {
    if (!rejectReason.trim()) {
      toast.error('Add a reason so the athlete can fix it.');
      return;
    }
    rejectMutation.mutate({ userId, reason: rejectReason.trim() });
  };

  return (
    <div className="max-w-4xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">NCAA Eligibility Verifications</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Athletes in your org{data.orgs.length > 1 ? 's' : ''} who have submitted an NCAA ID and
          are waiting on your verification.
          {data.orgs.length > 0 && (
            <>
              {' Scope: '}
              <span className="font-medium">{data.orgs.map((o) => o.name).join(', ')}</span>
            </>
          )}
        </p>
      </div>

      {data.athletes.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-sm space-y-3">
            <div>
              <p className="font-medium text-foreground">All caught up — nothing to verify.</p>
              <p className="text-muted-foreground mt-1">
                Athletes in {data.orgs.length === 1 ? data.orgs[0].name : 'your org'} who add their
                NCAA Eligibility ID will show up here for one-click verification.
              </p>
            </div>
            <div className="pt-2 border-t text-xs text-muted-foreground">
              No athletes yet?{' '}
              <Link to="/settings/organizations" className="underline hover:text-foreground">
                Add members from /settings/organizations
              </Link>
              {' '}— invitees pick the athlete persona during onboarding, then add their NCAA ID.
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {data.athletes.map((a) => {
            const isRejecting = rejectingId === a.id;
            const isBusy =
              (verifyMutation.isPending && verifyMutation.variables?.userId === a.id) ||
              (rejectMutation.isPending && rejectMutation.variables?.userId === a.id);

            return (
              <Card key={a.id}>
                <CardHeader>
                  <div className="flex items-start gap-3">
                    {a.image ? (
                      // biome-ignore lint/performance/noImgElement: external image not from Next/Image-supported source
                      <img
                        src={a.image}
                        alt=""
                        className="h-10 w-10 rounded-full object-cover shrink-0"
                      />
                    ) : (
                      <div
                        aria-hidden
                        className="h-10 w-10 rounded-full bg-muted text-muted-foreground flex items-center justify-center text-sm font-medium shrink-0"
                      >
                        {(a.name?.[0] ?? '?').toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <CardTitle className="text-base">{a.name}</CardTitle>
                      <p className="text-xs text-muted-foreground">{a.emailMasked}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {[
                          a.organizationName,
                          a.sport,
                          a.classYear,
                          a.ncaaIdAddedAt ? `Submitted ${formatDate(a.ncaaIdAddedAt)}` : null,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </p>
                    </div>
                    <div className="shrink-0 text-right">
                      <p className="text-xs text-muted-foreground">NCAA ID</p>
                      <p className="font-mono text-sm">{a.ncaaId}</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {!isRejecting ? (
                    <div className="flex gap-2 justify-end">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setRejectingId(a.id);
                          setRejectReason('');
                        }}
                        disabled={isBusy}
                      >
                        Reject
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => handleVerify(a.id)}
                        disabled={isBusy}
                      >
                        {isBusy && verifyMutation.isPending ? 'Verifying…' : 'Verify'}
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Input
                        placeholder="Why doesn't the NCAA ID match? (sent to the athlete)"
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        autoFocus
                        maxLength={500}
                      />
                      <div className="flex gap-2 justify-end">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setRejectingId(null);
                            setRejectReason('');
                          }}
                          disabled={isBusy}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => handleRejectSubmit(a.id)}
                          disabled={isBusy}
                        >
                          {isBusy && rejectMutation.isPending ? 'Sending…' : 'Send rejection'}
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
